/**
 * Surface Resolver (Visualizer v2)
 * ================================
 *
 * Resolves a Surface's stored sections into fillable regions by CARVING each
 * FILL section (W&CB) with any overlapping INSERT section (SlatWall now;
 * openings later). Carving is computed here, never baked into stored geometry —
 * so moving an insert just re-resolves, and the picture-frame effect falls out
 * automatically.
 *
 * Subtracting one rectangle from another yields at most four sub-rectangles
 * (bottom / top / left / right strips). Multiple inserts are handled by folding
 * the subtraction over each insert.
 *
 * Branch-exclusive to `3d-visualizer-v3`.
 */

import { Section, Surface, sectionFillMode } from '../../types/sections';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Sub-foot tolerance so floating-point seams don't spawn slivers. */
const EPS = 1e-6;

export function rectOf(s: Section): Rect {
  return { x: s.x, y: s.y, width: s.width, height: s.height };
}

/** Axis-aligned intersection, or null when the two rects don't overlap in area. */
export function intersectRect(a: Rect, b: Rect): Rect | null {
  const x0 = Math.max(a.x, b.x);
  const y0 = Math.max(a.y, b.y);
  const x1 = Math.min(a.x + a.width, b.x + b.width);
  const y1 = Math.min(a.y + a.height, b.y + b.height);
  if (x1 - x0 <= EPS || y1 - y0 <= EPS) return null;
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * Subtract `hole` from `base`, returning the remaining area as up to four
 * non-overlapping rects. Returns `[base]` when they don't overlap, and `[]`
 * when the hole fully covers the base.
 */
export function subtractRect(base: Rect, hole: Rect): Rect[] {
  const inter = intersectRect(base, hole);
  if (!inter) return [base];

  const out: Rect[] = [];
  const bx1 = base.x + base.width;
  const by1 = base.y + base.height;
  const ix1 = inter.x + inter.width;
  const iy1 = inter.y + inter.height;

  // bottom strip (full base width, below the hole)
  if (inter.y - base.y > EPS) {
    out.push({ x: base.x, y: base.y, width: base.width, height: inter.y - base.y });
  }
  // top strip (full base width, above the hole)
  if (by1 - iy1 > EPS) {
    out.push({ x: base.x, y: iy1, width: base.width, height: by1 - iy1 });
  }
  // left strip (between the hole's bottom and top, left of the hole)
  if (inter.x - base.x > EPS) {
    out.push({ x: base.x, y: inter.y, width: inter.x - base.x, height: inter.height });
  }
  // right strip (between the hole's bottom and top, right of the hole)
  if (bx1 - ix1 > EPS) {
    out.push({ x: ix1, y: inter.y, width: bx1 - ix1, height: inter.height });
  }

  return out;
}

/** Subtract several holes from a base; returns non-overlapping remainder rects. */
export function subtractRects(base: Rect, holes: Rect[]): Rect[] {
  let pieces: Rect[] = [base];
  for (const hole of holes) {
    pieces = pieces.flatMap((p) => subtractRect(p, hole));
    if (pieces.length === 0) break;
  }
  return pieces;
}

export interface ResolvedFill {
  /** The original FILL section (e.g. a W&CB section). */
  section: Section;
  /** Fillable sub-rects after carving — empty if fully covered by inserts. */
  regions: Rect[];
  /**
   * What carved it: the rectangles lying over this fill (inserts, and any
   * earlier fill it overlaps). Empty for a fill nothing touches. The board
   * layout works from these rather than from `regions` — see courses.ts.
   */
  holes: Rect[];
}

export interface ResolvedSurface {
  surfaceId: string;
  /** FILL sections (W&CB) with their carved fillable regions. */
  fills: ResolvedFill[];
  /** INSERT sections (SlatWall), as placed. */
  inserts: Section[];
  /** The openings cut through the surface — they carve fills and inserts alike. */
  openings: Rect[];
}

/**
 * Resolve a surface: carve every FILL section by all INSERT sections and by
 * the openings. Insert sections are returned as placed; where an opening cuts
 * into one, `openings` says so (see courses.ts for how that is laid out).
 *
 * Each fill is also carved by the fills before it. Fills are not supposed to
 * overlap (validation refuses it), so for a sound layout this changes nothing —
 * but it means that a layout that slipped through can never be counted or
 * drawn twice over the same area.
 */
export function resolveSurface(surface: Surface): ResolvedSurface {
  const fills = surface.sections.filter((s) => sectionFillMode(s.productType) === 'fill');
  const inserts = surface.sections.filter((s) => sectionFillMode(s.productType) === 'insert');
  const openings: Rect[] = (surface.openings ?? []).map((o) => ({ x: o.x, y: o.y, width: o.width, height: o.height }));
  const holes = [...inserts.map(rectOf), ...openings];

  return {
    surfaceId: surface.id,
    fills: fills.map((section) => {
      const rect = rectOf(section);
      const over = holes.filter((h) => intersectRect(rect, h));
      const regions = subtractRects(rect, over);
      holes.push(rect);
      return { section, regions, holes: over };
    }),
    inserts,
    openings,
  };
}
